/**
 * Which workstation this browser is (imaging-capture.md 3.1 step 2, 3.2
 * "Which workstation is this PC"). Velora Desktop tells us through its
 * read-only bridge (`window.veloraDesktop.workstation`, apps/desktop
 * preload); a plain browser gets a random name kept in its own storage.
 *
 * Storage can be missing (private window, blocked site data): every read
 * and write is guarded, and capture works without it (nothing remembered).
 */

interface DesktopBridge {
  isDesktop?: boolean;
  workstation?: { name?: string; officeId?: string | null; profile?: string | null } | null;
}

export interface WorkstationInfo {
  /** "OP3-PC" on Velora Desktop, "browser-1a2b3c" in a plain browser. */
  name: string;
  /** Office the desktop app is provisioned for, when it is. */
  officeId: string | null;
  profile: string | null;
  desktop: boolean;
}

const KEY = 'velora.capture.workstation';

export function readStore(key: string): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function writeStore(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage blocked: nothing is remembered on this computer.
  }
}

export function readJson<T>(key: string): T | null {
  const raw = readStore(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function workstationInfo(
  win: unknown = typeof window === 'undefined' ? null : window,
): WorkstationInfo {
  const bridge = (win as { veloraDesktop?: DesktopBridge } | null)?.veloraDesktop;
  const ws = bridge?.isDesktop ? bridge.workstation : null;
  if (ws?.name) {
    return {
      name: ws.name,
      officeId: ws.officeId ?? null,
      profile: ws.profile ?? null,
      desktop: true,
    };
  }
  let name = readStore(KEY);
  if (!name || !/^browser-[a-z0-9]{6,12}$/.test(name)) {
    name = `browser-${Math.random().toString(36).slice(2, 10).padEnd(6, '0')}`;
    writeStore(KEY, name);
  }
  return { name, officeId: null, profile: null, desktop: false };
}
